import type Route from '@ember/routing/route';
import type { Registry as Services } from '@ember/service';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';

const ERROR_MIXED_GRANULARITY = 'targets.read.multiple_templates';
const ERROR_OUTDATED_FILTER = 'input.filters.set.deprecated';

interface Error {
  payload?: {
    errors?: { id: string }[];
  };
}

/**
 * Error handling function that is called in the catch function of RI API requests. Logs error to Sentry & shows alert with an error message
 */

export default async function handleRequestError(
  error: unknown,
  params: { reference: string },
  context: Route & {
    alerting: Services['alerting'];
    controller: {
      setSegmentationFilters: (filters: string) => void;
    };
    errorTracking: Services['error-tracking'];
    intl: Services['intl'];
  },
): Promise<void> {
  context.errorTracking.captureException(error);

  const { intl, alerting, controller } = context;
  const errorId = (error as Error)?.payload?.errors?.[0]?.id;

  let keyForText: string;
  let keyForTitle: string;

  switch (errorId) {
    case ERROR_MIXED_GRANULARITY: {
      // params.reference is either "budget" or "forecast"
      keyForText =
        params.reference === 'budget'
          ? tKey('revenue_insight.budget.error.mixed_granularity')
          : tKey('revenue_insight.forecast.error.mixed_granularity');
      keyForTitle = tKey('general.error_title');

      break;
    }

    case ERROR_OUTDATED_FILTER: {
      keyForText = tKey('revenue_insight.error.outdated_filter.text');
      keyForTitle = tKey('revenue_insight.error.outdated_filter.title');
      break;
    }

    default: {
      keyForText = tKey('general.error');
      keyForTitle = tKey('general.error_title');

      break;
    }
  }

  await alerting.showErrorAlert({
    buttons: {
      confirm: {
        text: intl.t(tKey('general.OK')).toString(),
      },
    },
    text: intl.t(keyForText).toString(),
    title: intl.t(keyForTitle).toString(),
  });

  switch (errorId) {
    case ERROR_MIXED_GRANULARITY: {
      // Reset DatePicker to default values when mixed granularity error occurs
      // @ts-expect-error: Property 'transitionTo' does not exist on type 'Context'.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      context.transitionTo({
        queryParams: {
          fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
          toDate: dayjs().endOf('month').format('YYYY-MM-DD'),
        },
      });
      break;
    }

    case ERROR_OUTDATED_FILTER: {
      // Clear all segmentation filters in case of outdated ones
      controller.setSegmentationFilters('');
      break;
    }

    default: {
      break;
    }
  }
}
